/**
 * The single source of truth for feature flags and trading verticals.
 *
 * To add a trading vertical:
 *   1. add a member to `FeatureFlagKey` and to the `TradingTypeKey` union (types.ts)
 *   2. add its `FEATURE_FLAGS` entry here
 *   3. add its `TRADING_TYPES` row here
 *   4. add its env line to `readRawFeatureFlagEnv()` (env.ts)
 * Steps 2-4 are enforced by the compiler — a missing one is a type error.
 */
import { InstrumentTypeResponse } from "../../codegen-api";
import {
  FeatureFlagDefinition,
  FeatureFlagKey,
  TradingTypeDefinition,
  TradingTypeKey,
} from "./types";

/**
 * Every flag with its default. `Record<FeatureFlagKey, ...>` makes a new flag
 * without a definition a compile error.
 */
export const FEATURE_FLAGS: Record<FeatureFlagKey, FeatureFlagDefinition> = {
  [FeatureFlagKey.PERPETUAL_FUTURES]: {
    key: FeatureFlagKey.PERPETUAL_FUTURES,
    description: "Perpetual futures trading (BTC-PERP, ETH-PERP, ...).",
    defaultEnabled: true,
  },
  [FeatureFlagKey.PERPETUAL_SPOT]: {
    key: FeatureFlagKey.PERPETUAL_SPOT,
    description:
      "Spot trading. Off by default; chains with a spot CLOB opt in via ChainConfig.tradingTypes.",
    defaultEnabled: false,
  },
  [FeatureFlagKey.OPTIONS]: {
    key: FeatureFlagKey.OPTIONS,
    description:
      "Options trading (options chain, options trade form, RFQ). Built but not offered to users.",
    defaultEnabled: false,
  },
  [FeatureFlagKey.PREDICTION_MARKET]: {
    key: FeatureFlagKey.PREDICTION_MARKET,
    description:
      "Prediction market trading. Not implemented yet — enabling this throws at boot.",
    defaultEnabled: false,
  },
  [FeatureFlagKey.NATIVE_CANDLE_CHART]: {
    key: FeatureFlagKey.NATIVE_CANDLE_CHART,
    description:
      "Draw the price chart from this platform's own indexed fills (GET /candles + the " +
      "`candles` websocket channel) instead of embedding TradingView's tv.js widget, which " +
      "charts a third-party venue (OKX/OANDA/NASDAQ) rather than our order book. ON by " +
      "default: a chart of someone else's prices, which does not move when this book " +
      "trades, is not a reasonable default for a trading page. Set " +
      "REACT_APP_FEATURE_NATIVE_CANDLE_CHART=false to go back to the tv.js embed. A market " +
      "with no fills of its own renders \"No trades yet\" rather than borrowing another " +
      "venue's candles.",
    defaultEnabled: true,
  },
  [FeatureFlagKey.ADVANCED_CHART_UI]: {
    key: FeatureFlagKey.ADVANCED_CHART_UI,
    description:
      "With NATIVE_CANDLE_CHART on, draw our own candles in TradingView Advanced Charts — the " +
      "self-hosted Charting Library in public/charting_library/ (drawing tools, indicators, " +
      "chart types, position/liquidation/order lines) — fed by the same GET /candles and " +
      "`candles` channel. Timeframes use the library's built-in interval menu, limited to the " +
      "resolutions the backend serves. Falls back to the plain lightweight-charts view when the library " +
      "bundle is not deployed. Set REACT_APP_FEATURE_ADVANCED_CHART_UI=false for the plain view.",
    defaultEnabled: true,
  },
  [FeatureFlagKey.PWA_UI]: {
    key: FeatureFlagKey.PWA_UI,
    description:
      "PWA install / 'sync with desktop' entry points (Header, MobileHeader, AccountModal).",
    defaultEnabled: false,
  },
  [FeatureFlagKey.TRANSFER_UI]: {
    key: FeatureFlagKey.TRANSFER_UI,
    description:
      "Fund transfer entry points (AccountModal, AccountDropdown, Portfolio, Collateral table).",
    defaultEnabled: true,
  },
  [FeatureFlagKey.CONVERT_UI]: {
    key: FeatureFlagKey.CONVERT_UI,
    description:
      "Collateral 'convert' entry points (Collateral table row action + mobile button).",
    defaultEnabled: false,
  },
  [FeatureFlagKey.USER_SETTINGS_UI]: {
    key: FeatureFlagKey.USER_SETTINGS_UI,
    description: "'User Settings' entry point in the account dropdown menu.",
    defaultEnabled: false,
  },
  [FeatureFlagKey.ISOLATED_MARGIN_ADJUST]: {
    key: FeatureFlagKey.ISOLATED_MARGIN_ADJUST,
    description:
      "Add/Remove isolated margin buttons. No on-chain function exists and the backend route is a stub, so the dialog changed nothing.",
    defaultEnabled: false,
  },
  [FeatureFlagKey.EDIT_ORDER]: {
    key: FeatureFlagKey.EDIT_ORDER,
    description:
      "Edit-order buttons (portfolio, mobile, order details). The backend has no POST /orders/:id.",
    defaultEnabled: false,
  },
  [FeatureFlagKey.API_KEYS_UI]: {
    key: FeatureFlagKey.API_KEYS_UI,
    description:
      "Settings -> API Keys tab. The backend implements no API-key creation or listing yet.",
    defaultEnabled: false,
  },
  [FeatureFlagKey.REFERRAL_REWARDS]: {
    key: FeatureFlagKey.REFERRAL_REWARDS,
    description:
      "Referral rewards: Claim button and Rewards History tab. NOT an " +
      "'unsupported' flag: GET /referral-rewards-history and /referral-statistics " +
      "are implemented and live (both answer 401, not 404). Off because " +
      "POST /claim-referral-rewards proxies an external referrals service " +
      "(config.referrals.url) that could not be confirmed configured on this " +
      "deployment, and the Claim button is the half that fails visibly. Verify " +
      "that, then turn this on.",
    defaultEnabled: false,
  },
  [FeatureFlagKey.SUBACCOUNTS_UI]: {
    key: FeatureFlagKey.SUBACCOUNTS_UI,
    description:
      "Subaccounts button in the account modal (also needs a chain with supportsSubaccounts).",
    defaultEnabled: false,
  },
};

/**
 * Trading verticals. `routeSegment` values match the URLs the app already
 * serves (`/perpetual/eth`, `/spot/btc`, `/option/eth`), so this registry does
 * not change any existing link.
 *
 * Typed as a full `Record<TradingTypeKey, ...>` so a key added to the union
 * without a definition is a compile error.
 */
export const TRADING_TYPES: Record<TradingTypeKey, TradingTypeDefinition> = {
  [FeatureFlagKey.PERPETUAL_FUTURES]: {
    key: FeatureFlagKey.PERPETUAL_FUTURES,
    routeSegment: "perpetual",
    instrumentType: InstrumentTypeResponse.Perpetual,
    labelKey: "perpetual_futures",
    shortLabelKey: "perpetual_futures_short",
    quoteLabelSuffix: "-USD",
    order: 10,
    implemented: true,
  },
  [FeatureFlagKey.PERPETUAL_SPOT]: {
    key: FeatureFlagKey.PERPETUAL_SPOT,
    routeSegment: "spot",
    instrumentType: InstrumentTypeResponse.Spot,
    labelKey: "perpetual_spot",
    shortLabelKey: "perpetual_spot_short",
    quoteLabelSuffix: "-USDC",
    order: 20,
    implemented: true,
  },
  [FeatureFlagKey.OPTIONS]: {
    key: FeatureFlagKey.OPTIONS,
    routeSegment: "option",
    instrumentType: InstrumentTypeResponse.Option,
    labelKey: "options",
    shortLabelKey: "options_short",
    quoteLabelSuffix: null,
    order: 30,
    implemented: true,
  },
  [FeatureFlagKey.PREDICTION_MARKET]: {
    key: FeatureFlagKey.PREDICTION_MARKET,
    routeSegment: "prediction",
    instrumentType: null,
    labelKey: "prediction_market",
    shortLabelKey: "prediction_market_short",
    quoteLabelSuffix: null,
    order: 40,
    implemented: false,
  },
};

/** Every vertical, ordered for display. */
export const TRADING_TYPE_LIST: readonly TradingTypeDefinition[] = Object.values(
  TRADING_TYPES
).sort((a, b) => a.order - b.order);

/**
 * Every backend instrument type maps to exactly one vertical. Typing this as a
 * full `Record` over `InstrumentTypeResponse` means a new generated instrument
 * type fails the build until a vertical claims it — there is deliberately no
 * "unknown instrument" fallback anywhere in the app.
 */
export const TRADING_TYPE_KEY_BY_INSTRUMENT: Record<
  InstrumentTypeResponse,
  TradingTypeKey
> = {
  [InstrumentTypeResponse.Perpetual]: FeatureFlagKey.PERPETUAL_FUTURES,
  [InstrumentTypeResponse.Spot]: FeatureFlagKey.PERPETUAL_SPOT,
  [InstrumentTypeResponse.Option]: FeatureFlagKey.OPTIONS,
};

export const TRADING_TYPE_KEYS: readonly TradingTypeKey[] =
  TRADING_TYPE_LIST.map((definition) => definition.key);

export const isTradingTypeKey = (
  key: FeatureFlagKey
): key is TradingTypeKey =>
  (TRADING_TYPE_KEYS as readonly FeatureFlagKey[]).includes(key);

export const getFeatureFlagDefinition = (
  key: FeatureFlagKey
): FeatureFlagDefinition => FEATURE_FLAGS[key];
